import { For, Show, type JSX } from 'solid-js'
import { PeriodPicker } from '../../../components/period-picker'
import type { ViewProps } from 'wdc-cube-solid'
import type { MonitoringScope } from 'mormaii-web-presentation/monitoring'

import { collaboratorCell } from '../../../components/collaborator-cells'
import { Button } from '../../../components/filters'
import { DataTable, Pager } from '../../../components/table'
import { Panel } from '../../../components/ui'

import Css from './monitoring.module.scss'

/**
 * Monitoramentos compartilhados.
 *
 * A tabela e a MESMA da lista de colaboradores - mesmo scope de linha, mesmas
 * celulas. O que muda e a populacao (aqui, so quem compartilhou com este
 * profissional) e o formulario: dois campos com sugestao e um periodo digitado.
 *
 * O periodo e DIGITADO em `dd/MM/yyyy`, e nao escolhido num calendario. E o que o
 * painel antigo faz, e quem usa a tela digita mais rapido do que navega dois meses
 * para tras num seletor.
 */
export function MonitoringView(props: ViewProps<MonitoringScope>): JSX.Element {
    const submit = (event: Event) => {
        event.preventDefault()
        void props.scope.onSubmit()
    }

    return (
        <div class={Css.screen} aria-busy={props.scope.loading}>
            {/*
                A faixa ESCURA de filtros, a mesma do Painel, da Adocao e Engajamento e do
                Relatorio de Sono. Ate 18/09/2026 esta tela usava um `Panel` branco - e
                era a unica das quatro com filtro que fazia isso.
            */}
            <form onSubmit={submit}>
                <section class={Css.filterBar}>
                    <div class={Css.haloTop} />
                    <div class={Css.haloBottom} />

                    <div class={Css.filterBarBody}>
                        <h2 class={Css.filterBarTitle}>Filtrar monitoramentos</h2>

                        {/*
                            Tudo numa linha so, e sem botao de aplicar: o filtro vale
                            sozinho conforme se mexe nele - o texto depois de parar de
                            digitar, o periodo na hora, porque escolher um atalho nao tem
                            "parar". Sobra o Limpar, empurrado para a direita.
                        */}
                        <div class={Css.controls}>
                            <Suggested
                                label="Nome"
                                placeholder="Busca parcial"
                                value={props.scope.name}
                                options={props.scope.nameOptions}
                                onChange={(value) => props.scope.onNameChanged(value)}
                            />
                            <Suggested
                                label="CPF"
                                placeholder="Busca parcial"
                                value={props.scope.cpf}
                                options={props.scope.cpfOptions}
                                error={props.scope.cpfError}
                                onChange={(value) => props.scope.onCpfChanged(value)}
                            />

                            <Show when={props.scope.period}>
                                {(period) => <PeriodPicker scope={period()} onDark class={Css.period} />}
                            </Show>

                            <Button variant="glass" class={Css.clear} onClick={() => void props.scope.onClearFilters()}>
                                Limpar
                            </Button>
                        </div>
                    </div>
                </section>
            </form>

            <Panel title="Pacientes que compartilharam o histórico" flush>
                <DataTable
                    columns={props.scope.columns.map((column) => ({
                        field: column.field,
                        label: column.label,
                        sortable: column.sortable,
                        sorted: column.sorted,
                        descending: column.descending,
                        onSort: column.onSort,
                        // As medicoes se arrastam para mudar de lugar, como em Colaboradores.
                        movable: column.movable,
                        onDrop: (source) => column.onDrop(source)
                    }))}
                    rows={props.scope.rows}
                    loading={props.scope.loading}
                    cell={collaboratorCell}
                    layout={props.scope.layout}
                    emptyText={
                        props.scope.anyFilterActive
                            ? 'Nenhum monitoramento encontrado com os filtros aplicados'
                            : 'Ninguém compartilhou o histórico com você ainda'
                    }
                />

                <Pager
                    page={props.scope.page}
                    pageCount={props.scope.pageCount}
                    pages={props.scope.pageWindow}
                    total={props.scope.total}
                    onPrevious={() => void props.scope.onPreviousPage()}
                    onNext={() => void props.scope.onNextPage()}
                    onGoTo={(page) => void props.scope.onGoToPage(page)}
                />
            </Panel>
        </div>
    )
}

/**
 * Um campo de texto com sugestoes e, quando ha, o erro embaixo.
 *
 * As sugestoes vao num `<datalist>` nativo: e um elemento so, o navegador cuida da
 * navegacao por teclado, e nao ha lista flutuante a posicionar. A alternativa seria
 * repetir aqui o que o `Popover` do seletor de empresa ja faz - mas la a lista e o
 * unico jeito de escolher, e aqui ela e ajuda para quem ja esta digitando.
 */
function Suggested(props: {
    label: string
    placeholder: string
    value: string
    options: readonly string[]
    error?: string
    onChange: (value: string) => void
}): JSX.Element {
    const listId = `sugestoes-${props.label.replace(/\W+/g, '-').toLowerCase()}`

    return (
        <label class={Css.field}>
            <span class={Css.label}>{props.label}</span>

            <input
                value={props.value}
                placeholder={props.placeholder}
                list={props.options.length > 0 ? listId : undefined}
                onInput={(event) => props.onChange(event.currentTarget.value)}
                class={Css.input}
                classList={{ [Css.invalid]: !!props.error }}
            />

            <Show when={props.options.length > 0}>
                <datalist id={listId}>
                    <For each={props.options}>{(option) => <option value={option} />}</For>
                </datalist>
            </Show>

            <Show when={props.error}>{(message) => <span class={Css.error}>{message()}</span>}</Show>
        </label>
    )
}
